/**
 * WhatsAppRecipients — manages a list of up to 5 phone numbers in E.164 format.
 * Shows opted-out numbers with a visual indicator.
 */

import { useState } from "react";
import { Input, Button, Tag, message, Typography } from "antd";
import { MaterialIcon } from "@/components/MaterialIcon";
import { BORDER, RADIUS, SECONDARY } from "@/styles/design-tokens";

const { Text } = Typography;

const MAX_RECIPIENTS = 5;

const E164_REGEX = /^\+[1-9]\d{6,14}$/;

interface WhatsAppRecipientsProps {
  recipients: string[];
  optedOut: string[];
  onChange: (recipients: string[]) => void;
}

export default function WhatsAppRecipients({
  recipients,
  optedOut,
  onChange,
}: WhatsAppRecipientsProps) {
  const [inputValue, setInputValue] = useState("");

  const handleAdd = () => {
    const phone = inputValue.trim();
    if (!phone) return;

    if (!E164_REGEX.test(phone)) {
      message.warning("Enter a valid phone number with country code (e.g. +39340...)");
      return;
    }

    if (recipients.includes(phone)) {
      message.warning("This number is already in the list");
      return;
    }

    if (recipients.length >= MAX_RECIPIENTS) {
      message.warning(`Maximum ${MAX_RECIPIENTS} recipients allowed`);
      return;
    }

    onChange([...recipients, phone]);
    setInputValue("");
  };

  const handleRemove = (phone: string) => {
    onChange(recipients.filter((r) => r !== phone));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const isOptedOut = (phone: string) => optedOut.includes(phone);

  return (
    <div>
      {/* Recipient list */}
      {recipients.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            marginBottom: 10,
          }}
        >
          {recipients.map((phone) => (
            <div
              key={phone}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                border: BORDER,
                borderRadius: RADIUS,
                background: isOptedOut(phone) ? "#fff7e6" : "#fff",
              }}
            >
              <MaterialIcon
                name="phone"
                size={16}
                style={{ color: SECONDARY, flexShrink: 0 }}
              />
              <Text
                style={{
                  flex: 1,
                  fontSize: 13,
                  color: isOptedOut(phone) ? SECONDARY : "#262626",
                  textDecoration: isOptedOut(phone) ? "line-through" : undefined,
                }}
              >
                {phone}
              </Text>
              {isOptedOut(phone) && (
                <Tag color="warning" style={{ margin: 0, fontSize: 11 }}>
                  Opted out
                </Tag>
              )}
              <Button
                type="text"
                size="small"
                icon={<MaterialIcon name="close" size={14} />}
                onClick={() => handleRemove(phone)}
                style={{ flexShrink: 0 }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Add input */}
      {recipients.length < MAX_RECIPIENTS && (
        <div style={{ display: "flex", gap: 8 }}>
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="+39340..."
            prefix={<MaterialIcon name="add" size={16} style={{ color: SECONDARY }} />}
            style={{ flex: 1 }}
          />
          <Button
            onClick={handleAdd}
            disabled={!inputValue.trim()}
          >
            Add
          </Button>
        </div>
      )}

      <Text style={{ fontSize: 11, color: SECONDARY, display: "block", marginTop: 6 }}>
        {recipients.length}/{MAX_RECIPIENTS} recipients. Recipients can reply STOP to unsubscribe.
      </Text>
    </div>
  );
}
